import md5 from 'md5';
import { useCallback, useRef, useState } from 'react';

import APIConfiguration from '@savgroup-front-common/configuration/api';
import { DOCUMENT_TYPES } from '@savgroup-front-common/constants';
import { AuthConfiguration } from '@/configuration';
import useSellerSelfcareConfiguration from '@/hooks/useSellerSelfcareConfiguration';

interface UseLogoArgs {
  sellerId: string;
}

const useLogo = ({ sellerId }: UseLogoArgs) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const { sellerConfiguration } = useSellerSelfcareConfiguration({ sellerId });

  const sellerUrl = sellerConfiguration?.defaultOwnerOrderUrl
    ? { href: sellerConfiguration?.defaultOwnerOrderUrl, isExternal: true }
    : {
        href: '/',
        isExternal: false,
      };

  const logoUrl = `${
    APIConfiguration.imageCDN
  }sellers/${sellerId}/${DOCUMENT_TYPES.LOGO.toLocaleLowerCase()}`;

  const hashedClientId = md5(AuthConfiguration.clientId);
  const logoFromUiStyles = `${APIConfiguration.uiStylesCDN}logos/${hashedClientId}`;
  const logoFallback = `${
    import.meta.env.VITE_STATIC_HASH
  }/images/logo-blank-rvl.svg`;

  const [listAlternatives, setListAlternatives] = useState(
    [logoUrl, logoFromUiStyles, logoFallback].filter((item) => item),
  );

  const [firstAlternative, ...rest] = listAlternatives;

  const onError = useCallback(() => {
    if (imgRef.current) {
      imgRef.current.onerror = rest.length > 0 ? onError : null;
      imgRef.current.src = firstAlternative;

      setListAlternatives(rest);
    }
  }, [firstAlternative, rest]);

  return {
    logoUrl: firstAlternative,
    sellerUrl,
    imgRef,
    onError,
  };
};

export default useLogo;
